import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, FilterBar, EmptyState, StatusBadge } from '../components/common';
import { TodoDetailsModal, EditTodoModal } from '../components/modals';
import { useTimeframes } from '../utils/timeframes';
import { filterByTimeframe, filterByStatus, getTimeframeCounts } from '../utils/filters';
import { getUserName, formatDate, isOverdue } from '../utils/formatters';
import { SORT_BY, TODO_STATUS, STATUS_FILTER, TIMEFRAME } from '../constants';

/**
 * My Tasks page - Shows current user's tasks
 */
const MyTodos = ({ user, initialTimeframe, initialStatus }) => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [sortBy, setSortBy] = useState(SORT_BY.MEETING);
  const [filterStatus, setFilterStatus] = useState(initialStatus || STATUS_FILTER.ALL);
  const [filterTimeframe, setFilterTimeframe] = useState(initialTimeframe || TIMEFRAME.ALL);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [editTodo, setEditTodo] = useState(null);

  const timeframes = useTimeframes();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [todosData, usersData] = await Promise.all([
        api.get('/todos/my'),
        api.get('/users/list')
      ]);
      setTodos(todosData);
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load todos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters using utility functions
  const filteredTodos = useMemo(() => {
    let filtered = filterByStatus(todos, filterStatus);
    filtered = filterByTimeframe(filtered, filterTimeframe);
    return filtered;
  }, [todos, filterStatus, filterTimeframe]);

  // Count tasks by timeframe
  const timeframeCounts = useMemo(() => {
    return getTimeframeCounts(todos);
  }, [todos]);

  // Group todos by meeting or topic
  const groupedTodos = useMemo(() => {
    const grouped = {};
    
    filteredTodos.forEach(todo => {
      if (sortBy === SORT_BY.MEETING) {
        const key = todo.meetingId || 'unassigned';
        const name = todo.meetingName || 'Unassigned';
        if (!grouped[key]) grouped[key] = { name, todos: [] };
        grouped[key].todos.push(todo);
      } else {
        const key = todo.topicId || 'unassigned';
        const name = todo.topicTitle || 'Unassigned';
        if (!grouped[key]) grouped[key] = { name, todos: [] };
        grouped[key].todos.push(todo);
      }
    });
    
    return grouped;
  }, [filteredTodos, sortBy]);

  // Status counts for FilterBar
  const statusCounts = {
    all: todos.length,
    active: todos.filter(t => t.status !== TODO_STATUS.DONE).length,
    completed: todos.filter(t => t.status === TODO_STATUS.DONE).length,
  };

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">My Tasks</h1>
      </div>

      {/* Sort buttons */}
      <div className="flex gap-1" style={{ marginBottom: '1rem' }}>
        <Button
          variant={sortBy === SORT_BY.MEETING ? 'primary' : 'secondary'}
          size="small"
          onClick={() => setSortBy(SORT_BY.MEETING)}
        >
          Sort by Meeting
        </Button>
        <Button
          variant={sortBy === SORT_BY.TOPIC ? 'primary' : 'secondary'}
          size="small"
          onClick={() => setSortBy(SORT_BY.TOPIC)}
        >
          Sort by Topic
        </Button>
      </div>

      {/* Filter bar - reusable component! */}
      <FilterBar
        statusFilter={filterStatus}
        timeframeFilter={filterTimeframe}
        timeframeCounts={timeframeCounts}
        statusCounts={statusCounts}
        onStatusChange={setFilterStatus}
        onTimeframeChange={setFilterTimeframe}
      />

      {/* Results */}
      {filteredTodos.length === 0 ? (
        <EmptyState
          icon="✓"
          title="No tasks found"
          message="No tasks match your current filter."
        />
      ) : (
        Object.entries(groupedTodos).map(([key, group]) => (
          <div key={key} style={{ marginBottom: '1.5rem' }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: 'var(--color-primary)',
              marginBottom: '0.5rem',
              paddingBottom: '0.25rem',
              borderBottom: '2px solid var(--color-primary)'
            }}>
              {group.name} ({group.todos.length})
            </h2>
            <table className="data-table todos-table">
              <thead>
                <tr>
                  <th>Task</th>
                  {sortBy === SORT_BY.MEETING ? <th>Topic</th> : <th>Meeting</th>}
                  <th>Due Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {group.todos.map(todo => {
                  const taskIsOverdue = isOverdue(todo.dueDate, todo.status);
                  return (
                    <tr
                      key={todo.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedTodo(todo)}
                    >
                      <td>{todo.title}</td>
                      {sortBy === SORT_BY.MEETING ? (
                        <td>{todo.topicTitle || '-'}</td>
                      ) : (
                        <td>{todo.meetingName || '-'}</td>
                      )}
                      <td style={{
                        color: taskIsOverdue ? 'var(--color-danger)' : 'inherit',
                        fontWeight: taskIsOverdue ? 'bold' : 'normal'
                      }}>
                        {formatDate(todo.dueDate)}{taskIsOverdue && ' ⚠️'}
                      </td>
                      <td>
                        <StatusBadge status={todo.status} type="todo" />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditTodo(todo);
                          }}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}

      {/* Modals */}
      {selectedTodo && (
        <TodoDetailsModal
          todoId={selectedTodo.id}
          onClose={() => setSelectedTodo(null)}
        />
      )}

      {editTodo && (
        <EditTodoModal
          todo={{
            ...editTodo,
            assignees: (editTodo.assignees || []).map(a => (typeof a === 'object' ? a.id : a)),
          }}
          users={users}
          canEdit={false}
          onClose={() => setEditTodo(null)}
          onSave={() => {
            setEditTodo(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

MyTodos.propTypes = {
  user: PropTypes.object.isRequired,
  initialTimeframe: PropTypes.string,
  initialStatus: PropTypes.string,
};

export default MyTodos;
